import { useCallback, useMemo } from 'react';
import dayjs from 'dayjs';

export const formatPhdSchoolYearLabel = (schoolYear) => {
  if (!schoolYear) {
    return '';
  }
  const start = dayjs(schoolYear.start_year).isValid()
    ? dayjs(schoolYear.start_year).format('YYYY')
    : '?';
  const end = dayjs(schoolYear.end_year).isValid()
    ? dayjs(schoolYear.end_year).format('YYYY')
    : '?';

  return `${start}/${end}`;
};

const resolveSelectableSchoolYears = (schoolYears) => {
  return (schoolYears || [])
    .map((existingYear) => ({
      value: existingYear.id,
      start: dayjs(existingYear.start_year).startOf('day'),
      end: dayjs(existingYear.end_year).endOf('day'),
      label: formatPhdSchoolYearLabel(existingYear),
      isVirtual: false,
    }))
    .sort((a, b) => b.start.valueOf() - a.start.valueOf());
};

export const usePhdSchoolYearOptions = (schoolYears) => {
  const selectableSchoolYears = useMemo(
    () => resolveSelectableSchoolYears(schoolYears),
    [schoolYears],
  );

  const schoolYearOptions = useMemo(() => {
    return selectableSchoolYears.map(({ value, label }) => ({
      value,
      label,
      isCurrent: Boolean(
        (schoolYears || []).find((year) => year.id === value)?.is_current,
      ),
    }));
  }, [schoolYears, selectableSchoolYears]);

  const schoolYearMap = useMemo(
    () =>
      new Map(
        (schoolYears || []).map((year) => [
          year.id,
          formatPhdSchoolYearLabel(year),
        ]),
      ),
    [schoolYears],
  );

  const ensureSchoolYearSelection = useCallback(
    async (value) => {
      const exists = selectableSchoolYears.some(
        (option) => option.value === value,
      );
      return exists ? value : null;
    },
    [selectableSchoolYears],
  );

  return {
    schoolYearOptions,
    schoolYearMap,
    ensureSchoolYearSelection,
    isCreatingSchoolYear: false,
    futureSchoolYears: [],
  };
};

export const createSchoolYearChangeHandler = (
  form,
  ensureSchoolYearSelection,
) => {
  return async (value) => {
    try {
      const resolved = await ensureSchoolYearSelection(value);
      form?.setFieldsValue({ phd_school_year_id: resolved ?? null });
    } catch (error) {
      form?.setFieldsValue({ phd_school_year_id: null });
      return null;
    }
  };
};
